import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import CustomSubscriptionDrawer from '@features/CustomSubscriptionDrawer';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { Switch } from '@components/Switch';
import { Select } from '@components/Select';
import { useTranslation } from '@hooks';
import { BILLING_TYPE_MAP } from '@constants/billing';
import { accountService } from '@services/account';
import { alertActions } from '@actions/alert';

const Wrapper = styled('div')({
    display: 'grid',
    gridGap: '24px',
    gridTemplateColumns: '1fr 1fr',
    marginBottom: '16px',
    '@media (max-width: 1024px)': {
        gridTemplateColumns: '1fr',
        maxWidth: 'none',
    },
});

const PulseAICredits = ({ account, usedCredits, availableCredits, additionalCreditsLeft }) => {
    const [customSubscriptionIsOpen, setCustomSubscriptionIsOpen] = useState(false);
    const [customerPortalIsLoading, setCustomerPortalIsLoading] = useState(false);

    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { control, setValue, watch } = useFormContext();

    const isMultipleStripeAcc = account?.subscriptions.length > 1;
    const subscription = account?.subscriptions[0];
    const BillingPlan = subscription?.billingPlanName;
    const billingExpired = account?.billingDiffInSeconds < 0;
    const purchaseIsDisabled = billingExpired || BillingPlan === BILLING_TYPE_MAP.TRIAL;

    const autoPayEnabled = watch('autoPayEnabled');

    const accountOptions = account?.subscriptions.map(sub => ({
        label: sub.stripeAccountName,
        value: sub.id,
    }));

    const onClickBuyCredits = () => {
        if (isMultipleStripeAcc) {
            setCustomSubscriptionIsOpen(true);
        } else {
            onBuyCredits(subscription.id);
        }
    };

    const onBuyCredits = async subscriptionId => {
        setCustomerPortalIsLoading(true);
        try {
            const data = await accountService.buyAdditionalCredits({
                subscriptionId: subscriptionId,
            });
            if (data.paymentUrl) {
                window.location = data.paymentUrl;
            }
        } catch (_) {
            dispatch(alertActions.error(t('apiErrors.something_wrong')));
        } finally {
            setCustomerPortalIsLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: '698px', marginBottom: '24px' }}>
            <Typography variant="h6" color="textPrimary" sx={{ marginBottom: '16px' }}>
                {t('SettingsGeneral.pulseAICredits')}
            </Typography>
            <Wrapper>
                <div>
                    <Typography variant="subtitle2">{t('SettingsGeneral.recurrentCredits')}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {t('SettingsGeneral.recurrentCreditsDescription')}
                    </Typography>
                </div>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '8px',
                        justifyContent: 'center',
                    }}
                >
                    <div style={{ position: 'relative', width: '100%', borderRadius: '2px', overflow: 'hidden' }}>
                        <div style={{ backgroundColor: '#E4EAEB', height: '4px' }}></div>
                        <div
                            style={{
                                backgroundColor: '#1F4C5C',
                                height: '4px',
                                position: 'absolute',
                                top: 0,
                                width: `${(usedCredits / availableCredits) * 100}%`,
                            }}
                        ></div>
                    </div>
                    <Typography variant="caption">{`${usedCredits}/${availableCredits}`}</Typography>
                </div>
            </Wrapper>
            <Wrapper>
                <div>
                    <Typography variant="subtitle2">{t('SettingsGeneral.additionalCredits')}</Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {t('SettingsGeneral.additionalCreditsDescription')}
                    </Typography>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption">
                        {t('SettingsGeneral.creditsLeft', { amount: additionalCreditsLeft })}
                    </Typography>
                    <Button variant="contained" onClick={onClickBuyCredits} disabled={purchaseIsDisabled}>
                        {t('SettingsGeneral.buyCredits')}
                    </Button>
                </div>
            </Wrapper>
            <Wrapper>
                <div>
                    <Controller
                        control={control}
                        name="autoPayEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={field.value}
                                handleChange={event =>
                                    setValue('autoPayEnabled', event.target.checked, {
                                        shouldValidate: true,
                                        shouldDirty: true,
                                    })
                                }
                                label={t('SettingsGeneral.enableAutoPay')}
                                disabled={purchaseIsDisabled}
                            />
                        )}
                    />
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(0, 0, 0, 0.6)' }}>
                        {t('SettingsGeneral.autoPayDescription')}
                    </Typography>
                </div>
            </Wrapper>
            {autoPayEnabled && (
                <>
                    {isMultipleStripeAcc && (
                        <Wrapper>
                            <Controller
                                control={control}
                                name="autoPaySubscriptionId"
                                render={({ field, fieldState: { error }, ref }) => (
                                    <Select
                                        {...field}
                                        ref={ref}
                                        label={t('SettingsGeneral.selectAccount')}
                                        options={accountOptions}
                                        required
                                        error={!!error}
                                        helperText={error ? t(error.message) : null}
                                        disabled={purchaseIsDisabled}
                                    />
                                )}
                            />
                        </Wrapper>
                    )}
                    <Wrapper>
                        <Controller
                            control={control}
                            name="autoPayCreditsLimit"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    label={t('SettingsGeneral.creditLimit')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                    disabled={purchaseIsDisabled}
                                />
                            )}
                        />
                        <Controller
                            control={control}
                            name="autoPayCreditsAmount"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Input
                                    ref={ref}
                                    inputProps={{ ...field }}
                                    label={t('SettingsGeneral.creditsToPurchase')}
                                    fullWidth
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    required
                                    disabled={purchaseIsDisabled}
                                />
                            )}
                        />
                    </Wrapper>
                </>
            )}
            {customSubscriptionIsOpen && (
                <CustomSubscriptionDrawer
                    title={t('SettingsGeneral.selectAccount')}
                    primaryAction={onBuyCredits}
                    actionDisabled={customerPortalIsLoading}
                    open={customSubscriptionIsOpen}
                    onClose={() => setCustomSubscriptionIsOpen(false)}
                />
            )}
        </div>
    );
};

export default PulseAICredits;
